import React, { useState } from 'react';
import { PageSubTitle } from 'Components/Titles';
import { RestartPlanChurned } from 'Features/MyAccount/RestartPlanChurned';
import { formatUnixToDateString, timestampToDate } from 'Utils/Format';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { PlanSelectionModal } from 'Features/Plans/PlanSelection/PlanSelection';
import CancelWarningModal from './CancelWarningModal';
import { getCurrentOfferText, getPlanDisplayPrice, getPlanFromList, getRenewalPriceFrequencyLabel } from 'Utils/Plans';
import { BF_HOLIDAY_RENEWAL_SKU } from 'Utils/Constants';
import { StubLabel } from 'Components/Texts';

export const WebSubscription = ({
  isPauseEnabled,
  routes,
  subTitle,
  onRestartPlan,
  subscription,
  toggleResumePlanModal,
  trackEditAccount,
  handleCancelPauseSubscription,
  nextBillInfo,
  activePlanSimplifiedName,
  activePlan,
  allPlans,
  openPlanSelectModal = false,
  navigate,
  state,
  payments,
}) => {
  const {
    isChurnedUser,
    isNewUser,
    isChangePlanAvailable,
    isChurnedRestartAvailable,
    restartChurnedUserDetails,
    hasActiveSubscription,
    pauseStartDate,
    pauseEndDate,
    isPendingPause,
    isPaused,
    isImmediateCancel,
    activeSubscriptionPlanName,
    activeSubscriptionId,
    validityTill,
    nextSubscription,
    purchaseType,
  } = subscription;

  // show "Resubscribe" if the user is a churned user with restart available
  // also show in the case where restart isn't available but the user is a churned gift user
  const isChurnedGiftUser = purchaseType === 'gift' && isChurnedUser;
  const showResubscribe = isChurnedRestartAvailable || isChurnedGiftUser;

  let currentOffer = getCurrentOfferText(subscription, activePlan, payments);

  let nextBillingDate, nextBillingPrice;
  // get "Renewal Price" and "Next Billing Date" from payments object, same as in
  // activeUserPlan.js
  if (payments?.nextBillInfo) {
    const { nextBillingDateTime, nextBillingAmount, planFrequency } = payments.nextBillInfo;
    nextBillingDate = nextBillingDateTime > 0 ? formatUnixToDateString(nextBillingDateTime) : null;
    if (nextBillingAmount)
      nextBillingPrice = getRenewalPriceFrequencyLabel(getPlanDisplayPrice(nextBillingAmount), planFrequency);
  }
  // if can't get date from nextBillInfo, fall back to using activePlan
  if (!nextBillingDate) nextBillingDate = formatUnixToDateString(activePlan?.freePromo?.nextBillingDateTime);
  if (subscription.isPendingCancel || purchaseType === 'gift') {
    nextBillingDate = formatUnixToDateString(validityTill);
    nextBillingDate = nextBillingDate ? `Your subscription will end on ${nextBillingDate}` : null;
    currentOffer = null;
  }

  let modalPlans = allPlans;
  if ((!allPlans || allPlans.length < 1) && activePlan) {
    modalPlans = [activePlan];
  }

  const [showPlanSelectModal, setShowPlanSelectModal] = useState(openPlanSelectModal);
  const [showCancelWarningModal, setShowCancelWarningModal] = useState(false);

  const renderPauseMessage = () => {
    const paused = <span className="pause">PAUSED</span>;
    const pauseDate = <span className="date">{timestampToDate(pauseStartDate)}</span>;
    const resumeDate = <span className="date">{timestampToDate(pauseEndDate)}</span>;

    if (isPendingPause) {
      return (
        <div className="pausedDescription" data-testid="pausedDescription">
          Your Fox Nation subscription will be {paused} on {pauseDate} and will automatically resume on {resumeDate}.{' '}
          You can still access your Fox Nation content until the PAUSED start date.
        </div>
      );
    }

    if (isPaused) {
      return (
        <div className="pausedDescription" data-testid="pausedDescription">
          Your Fox Nation subscription is {paused} and will be reactivated on {resumeDate}. Resume now and continue
          enjoying your favorite shows. To Change Plan, resume account first.
        </div>
      );
    }

    return null;
  };

  const handleResumePlanClick = () => {
    const { planPrice, nextBillingAmount } = nextBillInfo;
    const pageTraits = {
      page_start_source: 'my account',
      page_plan_name: activeSubscriptionPlanName,
      page_plan_price: payments.activeBillInfo?.planPrice || planPrice || nextBillingAmount,
      page_plan_id: activeSubscriptionId,
    };
    trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESUME_STARTED, pageTraits);
    toggleResumePlanModal(true);
  };

  const handleChangePlanClick = () => {
    trackEditAccount('Change Plan');
    setShowPlanSelectModal(true);
  };

  const onClosePlanSelectModal = () => {
    setShowPlanSelectModal(false);
    if (state) {
      const { showPlanSelectModal, ...newState } = state;
      navigate('', { state: newState, replace: true });
    }
  };

  const onCancelConfirm = () => {
    navigate(routes.cancelPlan);
    trackEditAccount('Cancel Plan');
  };

  const onCancel = () => {
    if (isImmediateCancel) {
      setShowCancelWarningModal(true);
      return;
    }
    onCancelConfirm();
  };

  const renderSubscriptionSection = () => {
    const rightSideLinks = [];

    if (showResubscribe && !subscription.isPendingCancel) {
      rightSideLinks.push(
        <div className="linksCol">
          <button
            className="profileLink restartChurnedPlan"
            onClick={() => {
              navigate(routes.signupPlans);
              trackEditAccount('Resubscribe');
            }}
          >
            Resubscribe
          </button>
        </div>
      );
    }
    if (subscription.isPendingCancel && !showResubscribe) {
      rightSideLinks.push(
        <div className="linksCol">
          <button
            className="profileLink"
            onClick={() => {
              onRestartPlan();
              trackEditAccount('Restart Plan');
            }}
          >
            Restart Plan
          </button>
        </div>
      );
    }
    if (isChangePlanAvailable) {
      rightSideLinks.push(
        <div className="linksCol">
          <button className="profileLink" onClick={handleChangePlanClick}>
            Switch Plan
          </button>
        </div>
      );
    }
    if (isPauseEnabled && !isPendingPause) {
      rightSideLinks.push(
        <div className="linksCol">
          <button
            className="profileLink"
            onClick={() => {
              navigate(routes.pauseSubscription);
              trackEditAccount('Pause Plan');
            }}
          >
            Pause Plan
          </button>
        </div>
      );
    }
    if (isPaused) {
      rightSideLinks.push(
        <div className="linksCol">
          <button type="button" className="resumeButton" onClick={handleResumePlanClick}>
            Resume Plan
          </button>
        </div>
      );
    }
    if (hasActiveSubscription && purchaseType !== 'coupon' && purchaseType !== 'gift') {
      rightSideLinks.push(
        <div className="linksCol">
          <button className="profileLink" onClick={onCancel}>
            Cancel Plan
          </button>
        </div>
      );
    }

    if (isChurnedUser || isNewUser) {
      return rightSideLinks.map((el, idx) => (
        <div className="sectionFlex infoRow" key={idx}>
          <div className="sectionFlex infoRowLeft">
            <div className="subscriptionName">
              {idx === 0 ? (
                <span className="stubValue">{activePlanSimplifiedName}</span>
              ) : (
                <span className="stubValue emptyStub"></span>
              )}
            </div>
          </div>
          {el}
        </div>
      ));
    }

    let rightSideIdx = 0;
    return (
      <>
        <div className="sectionFlex infoRow">
          <div className="sectionFlex infoRowLeft">
            <StubLabel className="profileLabel" text="Plan:" />
            <div className="subscriptionName">
              <span className="stubValue">{activePlanSimplifiedName}</span>
            </div>
          </div>
          {rightSideIdx < rightSideLinks.length && rightSideLinks[rightSideIdx++]}
        </div>
        {currentOffer && (
          <div className="sectionFlex infoRow">
            <div className="sectionFlex infoRowLeft">
              <StubLabel className="profileLabel" text="Current Offer:" />
              <div className="subscriptionName">
                <span className="stubValue">{currentOffer}</span>
              </div>
            </div>
            {rightSideIdx < rightSideLinks.length && rightSideLinks[rightSideIdx++]}
          </div>
        )}
        {nextBillingPrice && (
          <div className="sectionFlex infoRow">
            <div className="sectionFlex infoRowLeft">
              <StubLabel className="profileLabel" text="Renewal Price:" />
              <div className="subscriptionName">
                <span className="stubValue">{nextBillingPrice}</span>
              </div>
            </div>
            {rightSideIdx < rightSideLinks.length && rightSideLinks[rightSideIdx++]}
          </div>
        )}
        {nextBillingDate && (
          <div className="sectionFlex infoRow">
            <div className="sectionFlex infoRowLeft">
              <StubLabel className="profileLabel" text="Next Billing Date:" />
              <div className="subscriptionName">
                <span className="stubValue">{nextBillingDate}</span>
              </div>
            </div>
            {rightSideIdx < rightSideLinks.length && rightSideLinks[rightSideIdx++]}
          </div>
        )}
        {rightSideLinks
          .map((el, idx) => {
            if (idx < rightSideIdx) return;
            return (
              <div className="sectionFlex infoRow" key={idx}>
                <div className="sectionFlex infoRowLeft">
                  <div className="subscriptionName">
                    <span className="stubValue emptyStub"></span>
                  </div>
                </div>
                {el}
              </div>
            );
          })
          .filter((el) => el !== undefined)}
      </>
    );
  };

  // temporary, remove after Black Friday/Holiday 2022 renewal plan promotion
  // while the promo is active, make renewal the default preselected plan
  let preselectedPlan = getPlanFromList(BF_HOLIDAY_RENEWAL_SKU, allPlans);
  if (Object.keys(preselectedPlan).length === 0) preselectedPlan = null;

  return (
    <div className="sectionFlex membershipSection" data-testid="webSubscription">
      <div className="flexGrowOne settingsSectionLeft">
        <PageSubTitle text={subTitle} />
      </div>
      <div
        className={`sectionFlex flexDirCol flexGrowTwo settingsSectionRight ${
          isPendingPause || isPaused ? 'paused' : ''
        }`}
      >
        {showResubscribe && (
          <div className="sectionFlex">
            <div className="sectionFlex restartChurnedUserDetails">
              <RestartPlanChurned {...restartChurnedUserDetails} validityTill={validityTill} />
            </div>
          </div>
        )}
        {renderSubscriptionSection()}
        {(isPendingPause || isPaused) && <div className="sectionFlex sectionDivider">{renderPauseMessage()}</div>}
        {isPendingPause && (
          <div className="sectionFlex cancelPause">
            <button role="button" className="profileLink" onClick={handleCancelPauseSubscription}>
              Cancel Pause Request
            </button>
          </div>
        )}
      </div>
      <PlanSelectionModal
        show={showPlanSelectModal && modalPlans.length > 0}
        currentPlan={activePlan}
        upcomingPlan={nextSubscription ? getPlanFromList(nextSubscription.serviceID, allPlans) : null}
        validityTill={validityTill}
        plans={modalPlans}
        onClose={onClosePlanSelectModal}
        selectedPlan={preselectedPlan}
      />
      <CancelWarningModal
        show={showCancelWarningModal}
        onClose={() => setShowCancelWarningModal(false)}
        onConfirm={onCancelConfirm}
      />
    </div>
  );
};
